import get from 'lodash/get';
import { useIntl } from 'react-intl';
import { useCallback } from 'react';

import { checkIsPaperworkFlow } from '../utils';
import { formatDate } from '../../PatientHealthboard/utils';
import { formatToMonthDayYear } from '../../../utils/converters/dateConverters';

import useGenderOptions from '../../../hooks/selectOptions/useGenderOptions';
import useInsuranceRelationOptions from '../../../hooks/selectOptions/useInsuranceRelationOptions';

const useInsuranceInfo = () => {
  const { formatMessage } = useIntl();

  const { getGenderTitleByValue } = useGenderOptions(formatMessage);

  const {
    hideInsuranceRelationFieldOrInfo,
    getInsuranceRelationTitleById,
  } = useInsuranceRelationOptions();

  const getPayerAndVerificationDate = useCallback(
    (
      { payer, createdAt, isVerified },
      flowType,
      isAdditionalInsurance = false,
      skipInsurance = false,
    ) => {
      const isPaperworkFlowType = checkIsPaperworkFlow(flowType);

      const ignoreValues =
        skipInsurance && !isAdditionalInsurance && isPaperworkFlowType;

      const data = [
        {
          label: formatMessage({
            id: 'profile.section.insurance.healthPlan',
          }),
          value: ignoreValues ? undefined : payer && get(payer, 'name'),
        },
      ];

      if (!isPaperworkFlowType && !isAdditionalInsurance) {
        data.push({
          label: formatMessage({
            id: 'profile.section.insurance.verificationDate',
          }),
          value: ignoreValues
            ? undefined
            : createdAt && isVerified && formatDate(createdAt, 'MMM D, Y'),
        });
      }

      return data;
    },
    [],
  );

  const getMemberIdAndGroupNumber = useCallback(
    (
      { memberId, groupNumber },
      flowType,
      isAdditionalInsurance = false,
      skipInsurance = false,
    ) => {
      const isPaperworkFlowType = checkIsPaperworkFlow(flowType);

      const ignoreValues =
        skipInsurance && !isAdditionalInsurance && isPaperworkFlowType;

      return [
        {
          label: formatMessage({ id: 'fields.insurance.memberId' }),
          value: ignoreValues ? undefined : memberId,
        },
        {
          label: formatMessage({ id: 'fields.insurance.groupNumber' }),
          value: ignoreValues ? undefined : groupNumber,
        },
      ];
    },
    [],
  );

  const getPrimarySubscriberMainData = useCallback(
    ({ primarySubscriber }) => {
      if (!primarySubscriber) return [];

      const {
        dob,
        firstName,
        lastName,
        phoneNumber,
        gender,
        insuranceRelationId,
      } = primarySubscriber || {};

      const data = [
        {
          label: formatMessage({
            id: 'profile.fields.name',
          }),
          value: `${firstName || ''} ${lastName || ''}`.trim(),
        },
        {
          label: formatMessage({
            id: 'profile.fields.dob',
          }),
          value: formatToMonthDayYear(dob),
        },
        {
          label: formatMessage({ id: 'common.phoneNumber' }),
          value: phoneNumber,
        },
        {
          label: formatMessage({
            id: 'profile.fields.gender',
          }),
          value: getGenderTitleByValue(gender),
        },
      ];

      if (!hideInsuranceRelationFieldOrInfo) {
        data.push({
          label: formatMessage({
            id: 'profile.fields.relationship',
          }),
          value: getInsuranceRelationTitleById(insuranceRelationId),
        });
      }

      return data;
    },
    [hideInsuranceRelationFieldOrInfo],
  );

  const getPrimarySubscriberAddressData = useCallback(
    ({ primarySubscriber }) => {
      if (!primarySubscriber) return [];

      const { address1, address2, city, state, postalCode } = primarySubscriber;

      return [
        {
          label: formatMessage({
            id: 'profile.fields.address1',
          }),
          value: address1,
        },
        {
          label: formatMessage({
            id: 'profile.fields.address2',
          }),
          value: address2,
        },
        {
          label: formatMessage({ id: 'profile.fields.city' }),
          value: city,
        },
        {
          label: formatMessage({ id: 'profile.fields.onlyState' }),
          value: state,
        },
        {
          label: formatMessage({ id: 'profile.fields.postalCode' }),
          value: postalCode,
        },
      ];
    },
    [],
  );

  return {
    getPayerAndVerificationDate,
    getMemberIdAndGroupNumber,
    getPrimarySubscriberAddressData,
    getPrimarySubscriberMainData,
  };
};

export default useInsuranceInfo;



// WEBPACK FOOTER //
// ./src/components/InsurancesInfo/hooks/useInsuranceInfo.js